import type { ReportResponse, ReportSpec } from '../models/report.types'

import {
  MAX_PROCESSING_ATTEMPTS,
  ReportProcessingTimeoutError,
  isReportProcessing,
  resolveProcessingDelayMs,
  waitForAbortableDelay,
} from './reportProcessing'
import { resolveReportRequest, type ReportRequestContext } from './reportRequest'
import { reportRequestCacheKey, shareInFlightReportRequest } from './reportRequestCache'

export interface ReportTransport {
  fetchReport: (url: string, query: string, signal?: AbortSignal) => Promise<ReportResponse>
}

export interface FetchReportContext extends ReportRequestContext {
  signal?: AbortSignal
  /** Injected so the poll policy is testable without a clock; defaults to a real abortable sleep. */
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>
}

/**
 * The single transport funnel; endpoint/query decisions stay pure and independently testable.
 *
 * Concurrent identical reads are shared, so two sections asking the same question on one page cost
 * one controller call instead of two 120s calls (AC3, phase A). A joiner receives a promise DERIVED
 * from the owner's rather than the owner's own: sharing a request must not mean sharing a fate, and
 * the owner can abort it by unmounting (see `shareInFlightReportRequest`).
 */
export function fetchReportData(
  spec: ReportSpec,
  context: FetchReportContext,
  transport: ReportTransport,
): Promise<ReportResponse> {
  const request = resolveReportRequest(spec, context)
  const run = (): Promise<ReportResponse> => runReportRequest(request, context, transport)
  const key = reportRequestCacheKey(request, context.exportRequest)
  return key === null ? run() : shareInFlightReportRequest(key, run)
}

/**
 * Polls while the controller reports `processing: true` — v3 `stores/reportStore.ts:198-200`, which
 * v4 had dropped, so a computing report surfaced as a decode error and never resolved. Unlike v3's
 * `while (true)`, the loop is bounded (`MAX_PROCESSING_ATTEMPTS`) and every wait is abortable, so a
 * stuck controller can neither spin forever nor outlive the caller's signal.
 */
async function runReportRequest(
  request: { url: string; query: string },
  context: FetchReportContext,
  transport: ReportTransport,
): Promise<ReportResponse> {
  const wait = context.wait ?? waitForAbortableDelay

  let response = await transport.fetchReport(request.url, request.query, context.signal)
  for (let attempt = 1; isReportProcessing(response); attempt += 1) {
    // Bail before sleeping, not after: an aborted caller must not pay for another poll gap.
    if (context.signal?.aborted) return response
    if (attempt >= MAX_PROCESSING_ATTEMPTS) throw new ReportProcessingTimeoutError(attempt)
    await wait(resolveProcessingDelayMs(response), context.signal)
    if (context.signal?.aborted) return response
    response = await transport.fetchReport(request.url, request.query, context.signal)
  }
  return response
}
